import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { fetchBrowseListingsFromFirestore } from '../lib/browseListingsCache';
import type { Listing } from '@shared/types';

/** Single React Query cache for all published listings browse/filter UIs. */
export const PUBLISHED_LISTINGS_QUERY_KEY = ['published-listings'] as const;

type ListingsIndexPayload = {
  generatedAt?: string;
  count?: number;
  listings?: Listing[];
};

/**
 * Prefer the live Firestore browse cache (1 doc read, updated on admin save),
 * then the Hosting JSON dump, then a full published-listings query.
 */
async function fetchPublishedListings(): Promise<Listing[]> {
  try {
    const fromFs = await fetchBrowseListingsFromFirestore();
    if (fromFs && fromFs.length > 0) return fromFs;
  } catch {
    // Rules / offline — try Hosting dump next
  }

  try {
    const res = await fetch('/data/listings-index.json', { cache: 'no-cache' });
    if (res.ok) {
      const data = (await res.json()) as ListingsIndexPayload;
      if (Array.isArray(data.listings) && data.listings.length > 0) {
        return data.listings;
      }
    }
  } catch {
    // Hosting miss — use full collection below
  }

  const snap = await getDocs(
    query(collection(db, 'listings'), where('status', '==', 'published'), limit(500))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Listing[];
}

export function usePublishedListings() {
  return useQuery({
    queryKey: PUBLISHED_LISTINGS_QUERY_KEY,
    queryFn: fetchPublishedListings,
    // Short enough that public cards refresh soon after admin edits;
    // still avoids hammering Firestore (1 cached doc read).
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 60,
  });
}
